// src/ekd/StructureEditor.tsx - the SME edits a table's key (grain) or a join by PICKING columns, and sees at once
// what the data says: how unique the key is, how many child rows find their parent. Saving stores the columns.
import React, { useEffect, useMemo, useState } from "react";
import { ekd_columns, ekd_measureGrain, ekd_measureJoin, type EkdColumn, type GrainMeasure, type JoinMeasure } from "../api/ekdAPI";

const num = (n?: number | null) => (n == null ? "—" : Number(n).toLocaleString());
const nm = (c?: EkdColumn) => (c ? (c.label ? `${c.label} (${c.column})` : c.column) : "");

export default function StructureEditor({ entityId, kind, table, child, parent, initial, onSave, onCancel, busy }:
  { entityId: string; kind: "grain" | "join"; table?: string; child?: string; parent?: string; initial: any;
    onSave: (value: string) => void; onCancel: () => void; busy?: boolean; key?: any }) {
  const [cols, setCols] = useState<Record<string, EkdColumn[]>>({});
  const [err, setErr] = useState<string | null>(null);
  // grain
  const [picked, setPicked] = useState<string[]>(() =>
    kind === "grain" ? (Array.isArray(initial) ? initial : String(initial || "").split(",")).map((x: any) => String(x).split("::").pop()!.trim()).filter(Boolean) : []);
  const [q, setQ] = useState("");
  const [gm, setGm] = useState<GrainMeasure | null>(null);
  // join
  const [pairs, setPairs] = useState<string[][]>(() =>
    kind === "join" ? String(initial || "").split(",").map((p) => p.split("=").map((x) => x.trim())).filter((p) => p.length === 2 && p[0] && p[1]) : []);
  const [jm, setJm] = useState<JoinMeasure | null>(null);
  const [measuring, setMeasuring] = useState(false);

  useEffect(() => {
    const want = kind === "grain" ? [table || ""] : [child || "", parent || ""];
    want.filter(Boolean).forEach((t) => {
      ekd_columns(entityId, t).then((r) => setCols((c) => ({ ...c, [t.toUpperCase()]: r.columns || [] })))
        .catch((e: any) => setErr(String(e?.message || e)));
    });
  }, [entityId, kind, table, child, parent]);

  const tcols = cols[(table || "").toUpperCase()] || [];
  const ccols = cols[(child || "").toUpperCase()] || [];
  const pcols = cols[(parent || "").toUpperCase()] || [];
  const byName = (list: EkdColumn[], c: string) => list.find((x) => x.column === c);
  const shown = useMemo(() => {
    const s = q.trim().toUpperCase();
    return tcols.filter((c) => !picked.includes(c.column) && (!s || c.column.toUpperCase().includes(s) || String(c.label || "").toUpperCase().includes(s))).slice(0, 40);
  }, [tcols, picked, q]);

  const measure = async () => {
    setMeasuring(true); setErr(null);
    try {
      if (kind === "grain") setGm(await ekd_measureGrain(entityId, table || "", picked));
      else setJm(await ekd_measureJoin(entityId, child || "", parent || "", pairs.filter((p) => p[0] && p[1])));
    } catch (e: any) { setErr(String(e?.message || e)); }
    finally { setMeasuring(false); }
  };
  // measure the starting key / join once the columns are known
  useEffect(() => { if ((kind === "grain" && picked.length) || (kind === "join" && pairs.length)) measure(); }, []); // eslint-disable-line

  const value = kind === "grain" ? picked.join(", ") : pairs.filter((p) => p[0] && p[1]).map((p) => `${p[0]}=${p[1]}`).join(", ");
  const canSave = kind === "grain" ? picked.length > 0 : pairs.some((p) => p[0] && p[1]);

  return (
    <div className="se">
      <style>{CSS}</style>
      {err && <div className="se-err">{err}</div>}
      {kind === "grain" ? (
        <div>
          <div className="se-lab">Key of <b>{table}</b> - the columns that identify one row</div>
          <div className="se-chips">
            {picked.map((c) => (
              <span key={c} className="se-chip">{nm(byName(tcols, c)) || c}
                <button aria-label="remove" onClick={() => { setPicked((p) => p.filter((x) => x !== c)); setGm(null); }}>×</button></span>))}
            {!picked.length && <span className="se-d">no column yet</span>}
          </div>
          <input className="se-in" placeholder="Add a column - search by name or code" value={q} onChange={(e: any) => setQ(e.target.value)} />
          {q.trim() && (
            <div className="se-list">
              {shown.map((c) => (
                <button key={c.column} className="se-li" onClick={() => { setPicked((p) => [...p, c.column]); setQ(""); setGm(null); }}>
                  <b>{nm(c)}</b><span>{c.fill_pct}% filled · {num(c.distinct)} distinct · {c.unique_alone_pct}% unique alone</span></button>))}
            </div>)}
          {gm && (gm.ok ? (
            <div className={"se-res " + (gm.is_unique ? "ok" : "warn")}>
              {gm.is_unique ? `Unique: every one of the ${num(gm.rows)} rows has its own key.`
                : `${gm.unique_pct}% of the rows with a full key are unique - ${num(gm.duplicate_rows)} rows share a key (${num(gm.duplicate_groups)} groups)`
                  + (gm.rows_key_filled !== gm.rows ? `; ${num((gm.rows || 0) - (gm.rows_key_filled || 0))} rows have a blank key part` : "") + "."}
              {!gm.is_unique && (gm.example_duplicates || []).length > 0 && (
                <div className="se-d">e.g. {(gm.example_duplicates || []).slice(0, 3).map((r) => Object.values(r).join(" / ")).join(" · ")}</div>)}
            </div>) : <div className="se-err">{gm.reason}</div>)}
        </div>
      ) : (
        <div>
          <div className="se-lab">Join <b>{child}</b> → <b>{parent}</b> - pick the column pairs</div>
          {pairs.map((p, i) => (
            <div key={i} className="se-row">
              <select className="se-in" value={p[0]} onChange={(e: any) => { const v = e.target.value; setPairs((ps) => ps.map((x, j) => j === i ? [v, x[1]] : x)); setJm(null); }}>
                <option value="">{child} column…</option>
                {ccols.map((c) => <option key={c.column} value={c.column}>{nm(c)}</option>)}
              </select>
              <span className="se-eq">=</span>
              <select className="se-in" value={p[1]} onChange={(e: any) => { const v = e.target.value; setPairs((ps) => ps.map((x, j) => j === i ? [x[0], v] : x)); setJm(null); }}>
                <option value="">{parent} column…</option>
                {pcols.map((c) => <option key={c.column} value={c.column}>{nm(c)}</option>)}
              </select>
              <button className="se-x" aria-label="remove" onClick={() => { setPairs((ps) => ps.filter((_, j) => j !== i)); setJm(null); }}>×</button>
            </div>))}
          <button className="se-link" onClick={() => setPairs((ps) => [...ps, ["", ""]])}>+ add a column pair</button>
          {jm && (jm.ok ? (
            <div className={"se-res " + ((jm.match_pct || 0) >= 99 ? "ok" : "warn")}>
              {jm.match_pct}% of the {num(jm.child_rows_key_filled)} {child} rows find their {parent} row ({num(jm.unmatched_rows)} do not) · {jm.cardinality}
              {!jm.parent_key_unique && <div className="se-d">The {parent} side is not unique on these columns - one {child} row can match several {parent} rows. A key part may be missing.</div>}
              {(jm.suggested_pairs || []).map((s) => (
                <div key={s.child + s.parent} className="se-d">The data suggests also {s.child} = {s.parent} ({s.share_pct}% of the values match){" "}
                  <button className="se-link" onClick={() => { setPairs((ps) => [...ps, [s.child, s.parent]]); setJm(null); }}>add it</button></div>))}
            </div>) : <div className="se-err">{jm.reason}</div>)}
        </div>
      )}
      <div className="se-acts">
        <button className="se-btn out" disabled={measuring || !canSave} onClick={measure}>{measuring ? "Measuring…" : "Measure"}</button>
        <span style={{ flex: 1 }} />
        <button className="se-btn out" onClick={onCancel}>Cancel</button>
        <button className="se-btn" disabled={!canSave || !!busy} onClick={() => onSave(value)}>Save &amp; confirm</button>
      </div>
    </div>
  );
}

const CSS = `
.se{border:1px solid #d9e0ea;border-radius:10px;padding:10px 12px;margin-top:8px;background:#fbfcfe}
.se-lab{font-size:12.5px;color:#33424f;margin-bottom:6px}
.se-chips{display:flex;flex-wrap:wrap;gap:6px;margin-bottom:6px}
.se-chip{display:inline-flex;align-items:center;gap:4px;font-size:12px;border:1px solid #c9b3e6;background:#fff;border-radius:14px;padding:3px 4px 3px 10px}
.se-chip button,.se-x{all:unset;cursor:pointer;color:#8a97a3;font-size:14px;padding:0 5px}
.se-in{width:100%;box-sizing:border-box;border:1px solid #cfd6dc;border-radius:7px;padding:6px 8px;font:inherit;font-size:12.5px}
.se-list{border:1px solid #e1e5e9;border-radius:7px;max-height:200px;overflow:auto;margin-top:4px;background:#fff}
.se-li{all:unset;cursor:pointer;display:flex;gap:10px;padding:6px 9px;font-size:12px;width:100%;box-sizing:border-box;border-bottom:1px solid #f1f3f5}
.se-li span{color:#5b6b78}.se-li:hover{background:#f7f2fc}
.se-row{display:flex;gap:6px;align-items:center;margin-bottom:6px}.se-eq{color:#5b6b78}
.se-res{font-size:12.5px;border-radius:7px;padding:7px 9px;margin-top:8px}.se-res.ok{background:#e9f6ee;color:#166534}.se-res.warn{background:#fff7ed;color:#7c2d00}
.se-d{font-size:11.5px;color:#5b6b78;margin-top:3px}
.se-err{background:#fef2f2;color:#991b1b;border-radius:7px;padding:6px 9px;font-size:12px;margin:4px 0}
.se-acts{display:flex;gap:8px;margin-top:10px}
.se-btn{font-weight:600;font-size:12.5px;border-radius:8px;padding:6px 12px;cursor:pointer;border:1px solid transparent;font-family:inherit;background:#7500C0;color:#fff}
.se-btn.out{background:#fff;border-color:#cfd6dc;color:#33424f}.se-btn:disabled{opacity:.45;cursor:default}
.se-link{all:unset;cursor:pointer;color:#7500C0;font-size:12px;text-decoration:underline}
`;
